import { AxiosResponse } from 'axios';
import { ResponseWithPagination } from '@/types';
import {
  Account,
  Job,
  Benefit,
  JobNoteTopic,
  PaginationParams,
  SortParams,
  PersonType,
} from '@/types/api';
import { supabase } from '@/lib/supabase';
import { asResponse, paginationRange, buildPaginatedResponse } from '@/lib/supabase-helpers';

export type DeleteJobParams = {
  id: number | string;
};

export type JobResponse = ResponseWithPagination<Job[]>;

type JobRelationsParams = {
  accountId?: string;
  skillIds?: string[];
  benefitIds?: string[];
};

export type EditableJob = Partial<Job> &
  JobRelationsParams & {
    salaryRangeMin?: number | string;
    salaryRangeMax?: number | string;
  };

type UpdateJobParams = DeleteJobParams & EditableJob & JobRelationsParams;

type CreateJobParams = Partial<Job> & JobRelationsParams;

export type NewTopicParams = {
  jobId: string;
  name: string;
};

export type NewNoteParams = {
  jobId: string;
  userId: string;
  topicId: string;
  text: string;
};

export type UpdateNoteParams = NewNoteParams & {
  noteId: string;
};

export type DeleteNoteParams = Pick<UpdateNoteParams, 'jobId' | 'noteId'>;

const getAllJobsSorted = async ({
  take,
  page,
  column,
  order,
}: PaginationParams & SortParams): Promise<AxiosResponse<JobResponse>> => {
  const [from, to] = paginationRange(page, take);

  const { data, error, count } = await supabase
    .from('jobs')
    .select('*, job_techs(skill_id, skills(*)), job_benefits(benefit_id, benefits(*))', { count: 'exact' })
    .order((column || 'created_at').replace(/([A-Z])/g, '_$1').toLowerCase(), { ascending: order === 'asc' })
    .range(from, to);

  if (error) throw error;

  const totalCount = count || 0;

  return asResponse<JobResponse>(buildPaginatedResponse((data || []) as unknown as Job[], totalCount, page, take) as any);
};

const getAllJobs = async (): Promise<AxiosResponse<Job[]>> => {
  const { data, error } = await supabase
    .from('jobs')
    .select('*, job_techs(skill_id, skills(*)), job_benefits(benefit_id, benefits(*))')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return asResponse((data || []) as unknown as Job[]);
};

const getAllPartners = async (): Promise<AxiosResponse<Account[]>> => {
  const { data, error } = await supabase
    .from('accounts')
    .select('*')
    .order('name', { ascending: true });

  if (error) throw error;
  return asResponse((data || []) as unknown as Account[]);
};

const getJobByID = async (jobID: string): Promise<AxiosResponse<Job>> => {
  const { data, error } = await supabase
    .from('jobs')
    .select('*, job_techs(skill_id, skills(*)), job_benefits(benefit_id, benefits(*))')
    .eq('id', jobID)
    .single();

  if (error) throw error;
  return asResponse(data as unknown as Job);
};

const searchJob = async (term: string): Promise<AxiosResponse<Job[]>> => {
  const { data, error } = await supabase
    .from('jobs')
    .select('*, job_techs(skill_id, skills(*)), job_benefits(benefit_id, benefits(*))')
    .ilike('title', `%${term}%`)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return asResponse((data || []) as unknown as Job[]);
};

const deleteJob = async ({ id }: DeleteJobParams): Promise<AxiosResponse<string>> => {
  // Clean up relations first
  await supabase.from('job_techs').delete().eq('job_id', id);
  await supabase.from('job_benefits').delete().eq('job_id', id);
  await supabase.from('job_candidates').delete().eq('job_id', id);

  const { error } = await supabase
    .from('jobs')
    .delete()
    .eq('id', id);

  if (error) throw error;
  return asResponse(String(id));
};

const updateJob = async ({
  id,
  accountId,
  benefitIds,
  skillIds,
  ...fields
}: UpdateJobParams): Promise<AxiosResponse<Job>> => {
  const updatePayload: Record<string, any> = {};
  if (fields.title !== undefined) updatePayload.title = fields.title;
  if (fields.description !== undefined) updatePayload.description = fields.description;
  if (fields.status !== undefined) updatePayload.status = fields.status;
  if (fields.salaryRangeMin !== undefined) updatePayload.salary_range_min = fields.salaryRangeMin;
  if (fields.salaryRangeMax !== undefined) updatePayload.salary_range_max = fields.salaryRangeMax;
  if (accountId !== undefined) updatePayload.account_id = accountId;

  if (Object.keys(updatePayload).length > 0) {
    const { error } = await supabase
      .from('jobs')
      .update(updatePayload)
      .eq('id', id);

    if (error) throw error;
  }

  // Replace skills
  if (skillIds) {
    await supabase.from('job_techs').delete().eq('job_id', id);
    if (skillIds.length > 0) {
      const rows = skillIds.map((skillId) => ({ job_id: id, skill_id: skillId }));
      const { error } = await supabase.from('job_techs').insert(rows);
      if (error) throw error;
    }
  }

  // Replace benefits
  if (benefitIds) {
    await supabase.from('job_benefits').delete().eq('job_id', id);
    if (benefitIds.length > 0) {
      const rows = benefitIds.map((benefitId) => ({ job_id: id, benefit_id: benefitId }));
      const { error } = await supabase.from('job_benefits').insert(rows);
      if (error) throw error;
    }
  }

  // Return updated job
  return getJobByID(String(id));
};

const getAllBenefitsAndPerks = async (): Promise<AxiosResponse<Benefit[]>> => {
  const { data, error } = await supabase
    .from('benefits')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return asResponse((data || []) as unknown as Benefit[]);
};

const createBenefitOrPerk = async ({ description }: { description: string }): Promise<AxiosResponse<Benefit>> => {
  const { data, error } = await supabase
    .from('benefits')
    .insert({ description })
    .select()
    .single();

  if (error) throw error;
  return asResponse(data as unknown as Benefit);
};

const deleteBenefitOrPerk = async ({ id }: DeleteJobParams): Promise<AxiosResponse<void>> => {
  const { error } = await supabase
    .from('benefits')
    .delete()
    .eq('id', id);

  if (error) throw error;
  return asResponse(undefined as unknown as void);
};

const updateBenefitOrPerk = async ({ id, description }: Partial<Benefit>): Promise<AxiosResponse<Benefit>> => {
  const { data, error } = await supabase
    .from('benefits')
    .update({ description })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return asResponse(data as unknown as Benefit);
};

const createJob = async ({
  accountId,
  benefitIds,
  skillIds,
  ...fields
}: CreateJobParams): Promise<AxiosResponse<Job>> => {
  const insertPayload: Record<string, any> = {
    title: fields.title,
    description: fields.description,
    status: fields.status,
    salary_range_min: fields.salaryRangeMin,
    salary_range_max: fields.salaryRangeMax,
  };
  if (accountId) insertPayload.account_id = accountId;

  const { data, error } = await supabase
    .from('jobs')
    .insert(insertPayload)
    .select()
    .single();

  if (error) throw error;

  // Insert skills
  if (skillIds && skillIds.length > 0) {
    const rows = skillIds.map((skillId) => ({ job_id: data.id, skill_id: skillId }));
    await supabase.from('job_techs').insert(rows);
  }

  // Insert benefits
  if (benefitIds && benefitIds.length > 0) {
    const rows = benefitIds.map((benefitId) => ({ job_id: data.id, benefit_id: benefitId }));
    await supabase.from('job_benefits').insert(rows);
  }

  return asResponse(data as unknown as Job);
};

const addNewNoteToTopic = async ({ jobId, userId, topicId, text }: NewNoteParams): Promise<AxiosResponse<any>> => {
  const { data, error } = await supabase
    .from('job_notes')
    .insert({
      topic_id: topicId,
      author_id: userId,
      text,
    })
    .select()
    .single();

  if (error) throw error;
  return asResponse(data);
};

const updateNote = async ({ jobId, text, noteId }: UpdateNoteParams): Promise<AxiosResponse<any>> => {
  const { data, error } = await supabase
    .from('job_notes')
    .update({ text })
    .eq('id', noteId)
    .select()
    .single();

  if (error) throw error;
  return asResponse(data);
};

const deleteNote = async ({ jobId, noteId }: DeleteNoteParams): Promise<AxiosResponse<any>> => {
  const { error } = await supabase
    .from('job_notes')
    .delete()
    .eq('id', noteId);

  if (error) throw error;
  return asResponse(null);
};

export type UpdateTopicName = Pick<NewNoteParams, 'topicId'> & { name: string };
export type DeleteTopicType = Pick<NewNoteParams, 'topicId'>;

const getAllJobTopics = async (jobId: string): Promise<AxiosResponse<JobNoteTopic[]>> => {
  const { data, error } = await supabase
    .from('job_note_topics')
    .select('*')
    .eq('job_id', jobId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return asResponse((data || []) as unknown as JobNoteTopic[]);
};

const addNewTopic = async ({ jobId, name }: NewTopicParams): Promise<AxiosResponse<any>> => {
  const { data, error } = await supabase
    .from('job_note_topics')
    .insert({ job_id: jobId, name })
    .select()
    .single();

  if (error) throw error;
  return asResponse(data);
};

const updateTopicName = async ({ topicId, name }: UpdateTopicName): Promise<AxiosResponse<any>> => {
  const { data, error } = await supabase
    .from('job_note_topics')
    .update({ name })
    .eq('id', topicId)
    .select()
    .single();

  if (error) throw error;
  return asResponse(data);
};

const deleteTopic = async ({ topicId }: DeleteTopicType): Promise<AxiosResponse<any>> => {
  // Delete notes under this topic first
  await supabase.from('job_notes').delete().eq('topic_id', topicId);

  const { error } = await supabase
    .from('job_note_topics')
    .delete()
    .eq('id', topicId);

  if (error) throw error;
  return asResponse(null);
};

export type AddCandidatesToJobParams = {
  jobId: string;
  candidateIds: string[];
};

const addCandidatesToJob = async ({
  jobId,
  candidateIds,
}: AddCandidatesToJobParams): Promise<AxiosResponse<unknown>> => {
  const rows = candidateIds.map((candidateId) => ({
    job_id: jobId,
    profile_id: candidateId,
  }));

  const { data, error } = await supabase
    .from('job_candidates')
    .insert(rows)
    .select();

  if (error) throw error;
  return asResponse(data);
};

type GetJobCandidatesByStageProps = {
  jobId: string;
};

export type JobCandidateStage =
  | 'HIRED'
  | 'OFFER_MADE'
  | 'SUBMITTED_TO_PARTNER'
  | 'READY_TO_HIRE'
  | 'NOT_READY'
  | 'FAILED';

export type JobCandidatesByStage = { [stage in JobCandidateStage]: Array<PersonType> };

const getJobCandidatesByStage = async ({
  jobId,
}: GetJobCandidatesByStageProps): Promise<AxiosResponse<JobCandidatesByStage>> => {
  const { data, error } = await supabase
    .from('job_candidates')
    .select('*, profiles(*)')
    .eq('job_id', jobId);

  if (error) throw error;

  const grouped: JobCandidatesByStage = {
    HIRED: [],
    OFFER_MADE: [],
    SUBMITTED_TO_PARTNER: [],
    READY_TO_HIRE: [],
    NOT_READY: [],
    FAILED: [],
  };

  for (const row of data || []) {
    const stage = row.stage as JobCandidateStage;
    if (grouped[stage]) {
      grouped[stage].push(row.profiles as unknown as PersonType);
    }
  }

  return asResponse(grouped);
};

const removeCandidatesToJob = async ({
  jobId,
  candidateIds,
}: AddCandidatesToJobParams): Promise<AxiosResponse<unknown>> => {
  const { error } = await supabase
    .from('job_candidates')
    .delete()
    .eq('job_id', jobId)
    .in('profile_id', candidateIds);

  if (error) throw error;
  return asResponse(null);
};

type GetJobsByCandidateIdOptions = {
  candidateId: string;
  status?: 'OPEN' | 'CLOSED';
};

const getJobsByCandidateId = async ({
  candidateId,
  status,
}: GetJobsByCandidateIdOptions): Promise<AxiosResponse<Job[]>> => {
  // Get job_ids for this candidate
  const { data: candidateJobs, error: cjError } = await supabase
    .from('job_candidates')
    .select('job_id')
    .eq('profile_id', candidateId);

  if (cjError) throw cjError;

  const jobIds = (candidateJobs || []).map((cj: any) => cj.job_id);

  if (jobIds.length === 0) {
    return asResponse([]) as unknown as Promise<AxiosResponse<Job[]>>;
  }

  let query = supabase
    .from('jobs')
    .select('*, job_techs(skill_id, skills(*)), job_benefits(benefit_id, benefits(*))')
    .in('id', jobIds);

  if (status) {
    query = query.eq('status', status);
  }

  const { data, error } = await query.order('created_at', { ascending: false });

  if (error) throw error;
  return asResponse((data || []) as unknown as Job[]) as unknown as Promise<AxiosResponse<Job[]>>;
};

export default {
  getAllJobs,
  getAllJobsSorted,
  getJobByID,
  searchJob,
  deleteJob,
  getAllPartners,
  updateJob,
  getAllBenefitsAndPerks,
  createBenefitOrPerk,
  createJob,
  addNewTopic,
  addNewNoteToTopic,
  updateNote,
  updateTopicName,
  deleteTopic,
  deleteNote,
  getAllJobTopics,
  deleteBenefitOrPerk,
  updateBenefitOrPerk,
  addCandidatesToJob,
  getJobCandidatesByStage,
  removeCandidatesToJob,
  getJobsByCandidateId,
};
